import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { Avatar } from '../ui'

/**
 * Die Kopfzeile: links der Haushalt, rechts die Person als Zeichen.
 *
 * ⚠️ Der Auslöser trägt einen eigenen Namen. Ein Avatar ist ein Bild — ohne
 * `aria-label` meldet eine Sprachausgabe hier einen namenlosen Knopf.
 *
 * Das Kontomenü ist ein eigener ARIA-Menüknopf. Fokusführung, Pfeiltasten,
 * Home/End, Escape und Außenklick werden hier an einer Stelle getragen.
 */
export function TopAppBar({
  haushalt,
  name,
  farbe,
  vorschau = false,
  aktionen,
  children,
}: {
  haushalt: string
  name: string
  farbe: string
  vorschau?: boolean
  /**
   * Platz zwischen Haushaltsname und Kontomenü.
   *
   * ⚠️ Die Kopfzeile bleibt damit DARSTELLEND. Der erste Entwurf hat die
   * Glocke hier eingebaut - und sie lädt Daten, womit jede Verwendung der
   * Kopfzeile plötzlich einen QueryClient brauchte (ihre eigenen Tests sind
   * daran zerbrochen, und zwar zu Recht). Was Daten holt, hängt die Schale
   * ein.
   */
  aktionen?: ReactNode | undefined
  children?: ReactNode
}) {
  const menue = useRef<HTMLDivElement>(null)
  const ausloeser = useRef<HTMLButtonElement>(null)
  const [offen, setOffen] = useState(false)
  const ort = useLocation()

  useEffect(() => {
    setOffen(false)
  }, [ort.pathname])

  useEffect(() => {
    const schliessen = (event: PointerEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return
        ausloeser.current?.focus()
      }
      if (event instanceof PointerEvent && menue.current?.contains(event.target as Node)) return
      setOffen(false)
    }
    document.addEventListener('pointerdown', schliessen)
    document.addEventListener('keydown', schliessen)
    return () => {
      document.removeEventListener('pointerdown', schliessen)
      document.removeEventListener('keydown', schliessen)
    }
  }, [])

  const eintraege = () =>
    Array.from(menue.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []).filter(
      (element) => !element.hasAttribute('disabled'),
    )

  const menueTastatur = (event: React.KeyboardEvent) => {
    const items = eintraege()
    const aktuell = items.indexOf(document.activeElement as HTMLElement)
    if (event.key === 'Escape') {
      event.preventDefault()
      setOffen(false)
      ausloeser.current?.focus()
    } else if (event.key === 'Tab') {
      setOffen(false)
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      const ziel =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? items.length - 1
            : event.key === 'ArrowDown'
              ? (aktuell + 1 + items.length) % items.length
              : (aktuell - 1 + items.length) % items.length
      items[ziel]?.focus()
    }
  }

  const oeffnenUndFokussieren = (letztes = false) => {
    setOffen(true)
    requestAnimationFrame(() => {
      const items = eintraege()
      items[letztes ? items.length - 1 : 0]?.focus()
    })
  }

  return (
    <header className="kopf">
      <span className="kopf-haushalt">{haushalt}</span>
      {aktionen}
      <div className="konto-menue" ref={menue}>
        <button
          ref={ausloeser}
          type="button"
          className="konto-menue__ausloeser"
          aria-label={`Konto von ${name}`}
          aria-haspopup="menu"
          aria-expanded={offen}
          aria-controls="konto-menue-inhalt"
          onClick={() => setOffen((alt) => !alt)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              if (offen) setOffen(false)
              else oeffnenUndFokussieren()
            } else if (event.key === 'ArrowDown') {
              event.preventDefault()
              oeffnenUndFokussieren()
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              oeffnenUndFokussieren(true)
            }
          }}
        >
          <span className="person-chip">
            <Avatar name={name} farbe={farbe} />
            <span className="konto-name">
              {name}
              {vorschau ? ' — Ansicht' : ''}
            </span>
          </span>
        </button>
        {offen ? (
          <div
            className="konto-menue-inhalt"
            id="konto-menue-inhalt"
            role="menu"
            aria-label={`Konto von ${name}`}
            onKeyDown={menueTastatur}
          >
            {children}
          </div>
        ) : null}
      </div>
    </header>
  )
}
